import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, CloudIcon, RadioIcon, ServerIcon, PhoneCallIcon, PhoneForwardedIcon, ArrowRightIcon, SmartphoneIcon, UsersIcon, SettingsIcon } from 'lucide-react';
const voiceServices = [{
  icon: CloudIcon,
  title: 'UCaaS',
  description: 'A cloud-based service integrating voice, video, messaging, and collaboration tools into one platform. It offers clear advantages over on-premises PBX systems, supporting seamless communication and collaboration from anywhere and on any device, ideal for remote or hybrid work environments.',
  color: 'text-indigo-500',
  bg: 'bg-indigo-50'
}, {
  icon: PhoneCallIcon,
  title: 'VoIP / Hosted Voice',
  description: 'Move beyond traditional phone lines with our VoIP Services. Experience high-definition voice calls over the internet, reducing costs and increasing flexibility.',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: RadioIcon,
  title: 'SIP Trunks',
  description: 'SIP Trunks are a perfect way to leverage the latest VoIP technologies, including business continuity features that ensure you never miss a call, even in a disaster.',
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}, {
  icon: ServerIcon,
  title: 'PRI Trunks',
  description: 'For businesses that require reliable, high-capacity voice communications, PRI Trunks offer robust connections and enhanced security.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}, {
  icon: PhoneForwardedIcon,
  title: 'Digital Phone Lines',
  description: "Get dedicated phone lines with advanced features such as call forwarding, voicemail, and caller ID. Enhance your team's communication and maintain a professional image.",
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function BusinessPhonePage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-508">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-509">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-510"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-511"></div>
        <div className="absolute top-0 right-1/3 -translate-y-1/4 w-[600px] h-[600px] bg-emerald-500/12 rounded-full blur-[120px] pointer-events-none" data-id="element-512"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-513">
          <div className="max-w-3xl" data-id="element-514">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-515">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-516">
                <span className="flex h-2 w-2 rounded-full bg-emerald-400 mr-2" data-id="element-517"></span>
                Modern Voice Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-518">
                POTS Replacement &<br className="hidden md:block" data-id="element-519" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400" data-id="element-520">
                  Business Phone
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl" data-id="element-521">
                At Ambient Telecom, we believe strong communication is key to
                business success. As copper phone lines face rising costs and
                eventual decommissioning, we offer modern, cost-effective
                alternatives to meet your current and future phone service
                needs.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-522">
                Get a Free Consultation
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-523" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cloud-Based Benefits */}
      <section className="py-20 bg-white" data-id="element-524">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-525">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-526">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-527">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-528">
                Stay connected from anywhere with our cloud-based phone services
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-529">
                Empower your team to stay in touch, whether they're at their
                desks or on the go, with seamless call mobility. Employees can
                personalize their features with ease, while administrators
                maintain full control of the system through an intuitive,
                user-friendly portal.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-530">
                {[{
                icon: SmartphoneIcon,
                label: 'Call Mobility',
                desc: 'Work from any device'
              }, {
                icon: UsersIcon,
                label: 'Team Control',
                desc: 'Personalized features'
              }, {
                icon: SettingsIcon,
                label: 'Easy Admin',
                desc: 'Intuitive portal'
              }].map((item, i) => {
                const Icon = item.icon;
                return <div key={i} className="text-center" data-id="element-531">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3" data-id="element-532">
                        <Icon className="w-6 h-6 text-primary" data-id="element-533" />
                      </div>
                      <div className="text-sm font-bold text-slate-900" data-id="element-534">
                        {item.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-1" data-id="element-535">
                        {item.desc}
                      </div>
                    </div>;
              })}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-536">
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-537">
                <img src="https://images.unsplash.com/photo-1596524430615-b46475ddff6e?w=800&h=600&fit=crop" alt="Business phone system" className="w-full h-auto object-cover" data-id="element-538" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 to-transparent" data-id="element-539"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-4 md:left-8 bg-white rounded-2xl shadow-xl p-4 border border-slate-100" data-id="element-540">
                <div className="flex items-center gap-3" data-id="element-541">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center" data-id="element-542">
                    <PhoneIcon className="w-5 h-5 text-emerald-600" data-id="element-543" />
                  </div>
                  <div data-id="element-544">
                    <div className="text-sm font-bold text-slate-900" data-id="element-545">
                      HD Voice
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-546">
                      Crystal clear calls
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voice Services */}
      <section className="py-20 bg-slate-50" data-id="element-547">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-548">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-549">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-550">
              Our Voice Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-551">
              Advanced voice services providing clear, reliable, and efficient
              solutions tailored to your specific needs.
            </p>
          </div>

          <motion.div className="space-y-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-552">
            {voiceServices.map((service, i) => {
            const Icon = service.icon;
            const isEven = i % 2 === 0;
            return <motion.div key={i} variants={itemVariants} className={`bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-553">
                  <div className="flex-1 p-8 md:p-10 flex flex-col justify-center" data-id="element-554">
                    <div className={`w-12 h-12 ${service.bg} rounded-xl flex items-center justify-center mb-5`} data-id="element-555">
                      <Icon className={`w-6 h-6 ${service.color}`} data-id="element-556" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-557">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-558">
                      {service.description}
                    </p>
                  </div>
                  <div className={`flex-shrink-0 w-full md:w-2 ${service.bg}`} data-id="element-559"></div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* POTS Replacement Highlight */}
      <section className="py-16 bg-white" data-id="element-560">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-561">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 md:p-16 relative overflow-hidden" data-id="element-562">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/15 rounded-full blur-[80px] pointer-events-none" data-id="element-563"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/10 rounded-full blur-[60px] pointer-events-none" data-id="element-564"></div>
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-565">
              <div data-id="element-566">
                <div className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-3" data-id="element-567">
                  Why Switch?
                </div>
                <h3 className="text-3xl font-bold text-white mb-4" data-id="element-568">
                  Copper lines are being decommissioned
                </h3>
                <p className="text-slate-300 leading-relaxed" data-id="element-569">
                  As copper phone lines supporting Plain Old Telephone Service
                  (POTS) face rising costs and eventual decommissioning, now is
                  the time to transition to modern, cost-effective alternatives
                  that deliver better quality and reliability.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-570">
                {[{
                value: '70%',
                label: 'Cost savings vs POTS'
              }, {
                value: 'HD',
                label: 'Voice quality'
              }, {
                value: '99.99%',
                label: 'Uptime guarantee'
              }, {
                value: '24/7',
                label: 'Support included'
              }].map((stat, i) => <div key={i} className="bg-white/5 rounded-xl p-4 text-center border border-white/10" data-id="element-571">
                    <div className="text-2xl font-extrabold text-emerald-400" data-id="element-572">
                      {stat.value}
                    </div>
                    <div className="text-xs text-slate-400 mt-1" data-id="element-573">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-574">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-575">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-576"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-577"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-578">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-579">
            Elevate Your Business Phone System
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-580">
            Your business communication deserves the best, and Ambient Telecom
            is here to deliver. Whether you're looking to enhance connectivity,
            improve reliability, or streamline your communication systems — we
            have the tailored voice solutions you've been looking for.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-581">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-582">
              Contact Us Today
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-583" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-584">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}